import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiSearch, FiCopy, FiToggleLeft, FiDatabase,
  FiLayers, FiClock, FiX, FiTrash2, FiSettings, FiBriefcase,
} from 'react-icons/fi';
import { parametersAPI, companiesAPI } from '../../services/api';
import CompanyScopeSelect, { companyScopeLabel } from '../../components/CompanyScopeSelect/CompanyScopeSelect';
import {
  fetchFieldSchemas,
  duplicateFieldSchema,
  deactivateFieldSchema,
  deleteFieldSchema,
} from '../../store/slices/fieldsManagerSlice';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import { formatDate as formatDateIntl } from '../../utils/dateFormat';
import { getLocalized } from '../../i18n/getLocalized';
import ConfirmDialog from '../../components/ConfirmDialog';
import './SchemaList.scss';

// `embedded` = rendered inside the Contract Settings panel, which already
// shows this page's standard header.
function SchemaList({ embedded = false }) {
  const { t, i18n } = useTranslation();
  // Old/imported schemas may carry multilang {en,tr} name/description objects;
  // resolve them to a string so React can render them.
  const nameOf = (s) => getLocalized(s?.name, i18n.language) || t('schemas.untitled', 'Untitled schema');
  const descOf = (s) => getLocalized(s?.description, i18n.language);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading } = useSelector((s) => s.fieldsManager);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // { type: 'delete' | 'deactivate', schema } while a confirmation is open
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // Company scope editing ("Tüm Şirketler / belirli şirketler")
  const [companies, setCompanies] = useState([]);
  const [scopeTarget, setScopeTarget] = useState(null);
  const [scopeIds, setScopeIds] = useState([]);
  const [scopeBusy, setScopeBusy] = useState(false);

  useEffect(() => {
    companiesAPI.list().then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, []);

  const openScope = (schema, e) => {
    e?.stopPropagation();
    setScopeTarget(schema);
    setScopeIds((schema.companyIds || []).map(v => String(v?._id || v)));
  };

  const saveScope = async () => {
    if (!scopeTarget) return;
    setScopeBusy(true);
    try {
      await parametersAPI.fieldSchemas.update(scopeTarget._id, { companyIds: scopeIds });
      toast.success(t('schemas.messages.scopeUpdated'));
      setScopeTarget(null);
      loadSchemas();
    } catch {
      toast.error(t('schemas.messages.scopeUpdateFailed'));
    } finally {
      setScopeBusy(false);
    }
  };

  const loadSchemas = useCallback(() => {
    dispatch(fetchFieldSchemas());
  }, [dispatch]);

  useEffect(() => {
    loadSchemas();
  }, [loadSchemas]);

  const stats = useMemo(() => ({
    all: items.length,
    active: items.filter((s) => s.isActive).length,
    inactive: items.filter((s) => !s.isActive).length,
  }), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (statusFilter === 'active' && !s.isActive) return false;
      if (statusFilter === 'inactive' && s.isActive) return false;
      if (!q) return true;
      return (
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    });
  }, [items, search, statusFilter]);

  const handleNew = () => navigate('/fields-manager');
  const handleEdit = (schema) => navigate(`/fields-manager/${schema._id}`);

  const handleDuplicate = async (schema, e) => {
    e?.stopPropagation();
    try {
      await dispatch(duplicateFieldSchema(schema._id)).unwrap();
      toast.success(t('schemas.messages.duplicated'));
    } catch (err) {
      toast.error(t('schemas.messages.duplicateFailed'));
    }
  };

  const handleDeactivate = (schema, e) => {
    e?.stopPropagation();
    setConfirmAction({ type: 'deactivate', schema });
  };

  const handleDelete = (schema, e) => {
    e?.stopPropagation();
    setConfirmAction({ type: 'delete', schema });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, schema } = confirmAction;
    setConfirmBusy(true);
    try {
      if (type === 'delete') {
        await dispatch(deleteFieldSchema(schema._id)).unwrap();
        toast.success(t('schemas.messages.deleted'));
      } else {
        await dispatch(deactivateFieldSchema(schema._id)).unwrap();
        toast.success(t('schemas.messages.deactivated'));
      }
      setConfirmAction(null);
    } catch (err) {
      if (err?.code === 'STANDARD_TEMPLATE') {
        toast.error(t(
          type === 'delete' ? 'schemas.messages.standardNoDelete' : 'schemas.messages.standardNoDeactivate',
          { defaultValue: 'Standard templates cannot be modified this way — duplicate them instead.' }
        ));
      } else if (type === 'delete' && err?.code === 'FIELD_SCHEMA_IN_USE') {
        toast.error(
          err.contractCount > 0
            ? t('schemas.messages.inUseCount', { count: err.contractCount })
            : t('schemas.messages.inUse')
        );
      } else {
        toast.error(t(type === 'delete' ? 'schemas.messages.deleteFailed' : 'schemas.messages.deactivateFailed'));
      }
      setConfirmAction(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const formatDate = (dateStr) => formatDateIntl(dateStr);

  const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || '').join('') || 'S';
  };

  const filterTabs = [
    { key: 'all', label: t('common.all', 'All'), count: stats.all },
    { key: 'active', label: t('common.active'), count: stats.active },
    { key: 'inactive', label: t('common.inactive'), count: stats.inactive },
  ];

  return (
    <div className="schema-list-page">
      <div className="schema-list-page__header">
        {/* Embedded under Contract Settings, the panel's standard page header
            already carries this page's title. */}
        {!embedded && (
          <PageHeader
            label={t('nav.businessConfig', 'Contract Settings')}
            labelIcon={<FiSettings />}
            title={t('schemas.title')}
            description={t('pageDescriptions.schemas')}
          />
        )}
        <button className="btn btn-primary schema-list-page__new-btn" onClick={handleNew}>
          <FiPlus /> {t('schemas.newSchema')}
        </button>
      </div>

      <div className="schema-list-page__toolbar">
        <div className="schema-list-page__search">
          <FiSearch />
          <input
            type="text"
            placeholder={t('schemas.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="schema-list-page__search-clear"
              onClick={() => setSearch('')}
              aria-label={t('common.clear', 'Clear')}
            >
              <FiX />
            </button>
          )}
        </div>

        <div className="schema-list-page__tabs" role="tablist">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`schema-list-page__tab ${statusFilter === tab.key ? 'is-active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
              <span className="schema-list-page__tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="schema-list-page__grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="schema-card schema-card--skeleton" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="schema-list-page__empty">
          <div className="schema-list-page__empty-icon">
            <FiDatabase size={28} />
          </div>
          <h3>{t('schemas.noSchemasFound')}</h3>
          <p>{t('schemas.noSchemasDesc')}</p>
          <button className="btn btn-primary" onClick={handleNew}>
            <FiPlus /> {t('schemas.createFirstSchema')}
          </button>
        </div>
      ) : (
        <div className="schema-list-page__grid">
          {filtered.map((schema, idx) => {
            const sectionCount = schema.sections?.length ?? 0;
            return (
              <button
                key={schema._id}
                type="button"
                className={`schema-card ${schema.isActive ? '' : 'schema-card--inactive'}`}
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
                onClick={() => handleEdit(schema)}
              >
                <div className="schema-card__top">
                  <div className="schema-card__avatar" aria-hidden="true">
                    {getInitials(nameOf(schema))}
                  </div>
                  {schema.isStandard && (
                    <span className="schema-card__standard">
                      {t('schemas.standardBadge', { defaultValue: 'Standard' })}
                    </span>
                  )}
                  <span className={`schema-card__status ${schema.isActive ? 'is-active' : 'is-inactive'}`}>
                    <span className="schema-card__status-dot" />
                    {schema.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </div>

                <div className="schema-card__body">
                  <h3 className="schema-card__name">{nameOf(schema)}</h3>
                  <p className="schema-card__description">
                    {descOf(schema) || t('schemas.noDescription', 'No description provided.')}
                  </p>
                </div>

                <div className="schema-card__meta">
                  <span className="schema-card__meta-item">
                    <FiLayers />
                    {t('schemas.sectionsCount', { count: sectionCount })}
                  </span>
                  <span className="schema-card__meta-item">
                    <FiBriefcase />
                    {companyScopeLabel(schema.companyIds, companies, t)}
                  </span>
                  <span className="schema-card__meta-item">
                    <FiClock />
                    {formatDate(schema.updatedAt)}
                  </span>
                </div>

                <div className="schema-card__actions">
                  <span
                    role="button"
                    tabIndex={0}
                    className="schema-card__action"
                    title={t('schemas.duplicate')}
                    onClick={(e) => handleDuplicate(schema, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDuplicate(schema, e); }}
                  >
                    <FiCopy />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="schema-card__action"
                    title={t('companyScope.label')}
                    onClick={(e) => openScope(schema, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') openScope(schema, e); }}
                  >
                    <FiBriefcase />
                  </span>
                  {schema.isActive && !schema.isStandard && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="schema-card__action schema-card__action--danger"
                      title={t('common.deactivate')}
                      onClick={(e) => handleDeactivate(schema, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleDeactivate(schema, e); }}
                    >
                      <FiToggleLeft />
                    </span>
                  )}
                  {/* Standard system templates can be duplicated, never deleted. */}
                  {!schema.isStandard && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="schema-card__action schema-card__action--danger"
                      title={t('common.delete')}
                      onClick={(e) => handleDelete(schema, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(schema, e); }}
                    >
                      <FiTrash2 />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {scopeTarget && (
        <div className="schema-scope-modal__overlay" onClick={() => setScopeTarget(null)}>
          <div className="schema-scope-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="schema-scope-modal__head">
              <h3>{t('companyScope.label')}</h3>
              <p>{nameOf(scopeTarget)}</p>
              <button type="button" className="schema-scope-modal__close" onClick={() => setScopeTarget(null)} aria-label={t('common.cancel')}>
                <FiX />
              </button>
            </div>
            <div className="schema-scope-modal__body">
              <CompanyScopeSelect value={scopeIds} onChange={setScopeIds} companies={companies} hideLabel />
            </div>
            <div className="schema-scope-modal__footer">
              <button type="button" className="btn btn-outline" onClick={() => setScopeTarget(null)}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-primary" disabled={scopeBusy} onClick={saveScope}>
                {scopeBusy ? t('common.saving', 'Saving…') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        variant={confirmAction?.type === 'delete' ? 'danger' : 'warning'}
        icon={confirmAction?.type === 'deactivate' ? FiToggleLeft : undefined}
        title={t(confirmAction?.type === 'delete' ? 'schemas.deleteTitle' : 'schemas.deactivateTitle')}
        message={
          confirmAction
            ? t(
                confirmAction.type === 'delete' ? 'schemas.confirmDelete' : 'schemas.confirmDeactivate',
                { name: nameOf(confirmAction.schema) }
              )
            : ''
        }
        confirmLabel={t(confirmAction?.type === 'delete' ? 'common.delete' : 'common.deactivate')}
        loading={confirmBusy}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

export default SchemaList;
